import * as THREE from 'three';
import { renderManager } from './globals.js';

const terrenoShaderUniforms = {
    uTime: { value: 0 }
};

/* COLOR TERRAIN ARENA */
export function color_terrain_arena(terrain, scene){

    terrain.geometry.computeBoundingBox();
    terrain.material.side = THREE.DoubleSide;

    const pos = terrain.geometry.attributes.position;

    const cubeObj = scene.getObjectByName("CUBE_SURFACE");
    let cubeY = 0;
    if (cubeObj) {
        cubeObj.updateMatrixWorld(true);
        const cubeBox = new THREE.Box3().setFromObject(cubeObj);
        cubeY = cubeBox.max.y;
    }

    let terrainMinY = Infinity;
    let terrainMaxY = -Infinity;
    const vertexWorld = new THREE.Vector3();

    for (let i = 0; i < pos.count; i++) {
        vertexWorld.set(pos.getX(i), pos.getY(i), pos.getZ(i));
        vertexWorld.applyMatrix4(terrain.matrixWorld);
        const y = vertexWorld.y;

        terrainMinY = Math.min(terrainMinY, y);
        terrainMaxY = Math.max(terrainMaxY, y);
    }

    const gradientColors = [
        new THREE.Color(0x0a3056),
        new THREE.Color(0x0a3056),
        new THREE.Color(0x4d8aa3),
        new THREE.Color(0x2f6b3f),
        new THREE.Color(0x2f6b3f),
        new THREE.Color(0x1e4620)
    ];

    const lut = [];
    const steps = gradientColors.length - 1;

    for (let i = 0; i < 256; i++) {
        const t = i / 255;
        const idx = Math.min(Math.floor(t * steps), steps - 1);
        const k = t * steps - idx;

        lut.push(
            new THREE.Color()
                .copy(gradientColors[idx])
                .lerp(gradientColors[idx + 1], k)
        );
    }

    const colors = [];
    const darkBlue = new THREE.Color(0x02224a);
    const FLAT_THRESHOLD = 0;

    for (let i = 0; i < pos.count; i++) {
        vertexWorld.set(pos.getX(i), pos.getY(i), pos.getZ(i));
        vertexWorld.applyMatrix4(terrain.matrixWorld);

        let t = (vertexWorld.y - terrainMinY) / (terrainMaxY - terrainMinY);

        if (t < FLAT_THRESHOLD) {
            colors.push(darkBlue.r, darkBlue.g, darkBlue.b);
            continue;
        }

        const t2 = THREE.MathUtils.clamp(Math.pow(t, 10), 0, 1);
        const col = lut[Math.floor(t2 * 255)];

        colors.push(col.r, col.g, col.b);
    }

    terrain.geometry.setAttribute(
        "color",
        new THREE.Float32BufferAttribute(colors, 3)
    );

    const loader = new THREE.TextureLoader();
    const textureBase = loader.load(
        "https://cozumel-reef-maps.com.mx/Views/Default/img/materials/sand_ligera.jpg"
    );

    textureBase.wrapS = THREE.RepeatWrapping;
    textureBase.wrapT = THREE.RepeatWrapping;
    textureBase.repeat.set(4, 4);

    terrain.material = new THREE.MeshStandardMaterial({
        map: textureBase,
        vertexColors: true,
        metalness: 0.0,
        roughness: 1.0,
        side: THREE.DoubleSide
    });

    terrain.material.onBeforeCompile = (shader) => {
        shader.uniforms.uTime = terrenoShaderUniforms.uTime;

        shader.fragmentShader = `
            uniform float uTime;
            
            float hash(vec2 p) {
                return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
            }
            float noise(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);
                vec2 u = f * f * (3.0 - 2.0 * f);
                return mix(mix(hash(i + vec2(0.0,0.0)), hash(i + vec2(1.0,0.0)), u.x),
                           mix(hash(i + vec2(0.0,1.0)), hash(i + vec2(1.0,1.0)), u.x), u.y);
            }
        ` + shader.fragmentShader;

        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <color_fragment>',
            `
            #include <color_fragment>

            if (vColor.g > 0.20 && vColor.g >= vColor.r) {

                vec2 uvNoise = vMapUv * 45.0; 
                float n1 = noise(uvNoise + vec2(uTime * 0.4, uTime * 0.2));
                float n2 = noise(uvNoise * 1.8 - vec2(uTime * 0.3, -uTime * 0.35));
                float viento = (n1 * 0.55 + n2 * 0.45);

                vec3 colorClaro = diffuseColor.rgb * 1.35; 
                vec3 colorOscuro = diffuseColor.rgb * 0.75;

                diffuseColor.rgb = mix(colorOscuro, colorClaro, viento);
            }
            `
        );
    };

    terrain.material.needsUpdate = true;
}

/* 🔥 POPULATE PALMS ECO - TOTALMENTE REESTRUCTURADO */
export function populate_palms_eco(terrain, scene, maxPalms = 800) {
    const geometry = terrain.geometry;
    const pos = geometry.attributes.position;
    const vertexColors = geometry.attributes.color;

    if (!vertexColors) {
        console.warn("⚠️ color_terrain_arena no ha terminado de pintar.");
        return null;
    }

    geometry.attributes.color.needsUpdate = true;

    const palmGeo = create_lowpoly_palm_geometry();
    const palmMaterial = create_wind_material();

    const instancedMesh = new THREE.InstancedMesh(palmGeo, palmMaterial, maxPalms);
    instancedMesh.frustumCulled = false; 
    instancedMesh.castShadow = true;
    instancedMesh.receiveShadow = true;

    terrain.add(instancedMesh); 

    const palmCandidates = [];

    // Selección robusta basada en la distribución de color real de la selva
    for (let i = 0; i < pos.count; i += 3) { 
        if (palmCandidates.length >= maxPalms) break;

        const r = vertexColors.getX(i);
        const g = vertexColors.getY(i);
        const b = vertexColors.getZ(i);

        // Filtro estricto: Solo donde predomina el canal verde sobre el azul y rojo (Selva alta)
        if (g > 0.25 && g > b && g >= r) {
            
            if (Math.random() > 0.15) continue;

            palmCandidates.push({
                position: new THREE.Vector3(
                    pos.getX(i),
                    pos.getY(i),
                    pos.getZ(i)
                ),
                rotationY: Math.random() * Math.PI * 2,
                baseScale: 0.003 + Math.random() * 0.003
            });
        }
    }

    // Plan de contingencia ante inversión asíncrona de buffers del importador FBX
    if (palmCandidates.length === 0) {
        console.warn("⚠️ Aplicando inversión adaptativa de filtro por coordenadas de exportación.");
        for (let i = 0; i < pos.count; i += 3) {
            if (palmCandidates.length >= maxPalms) break;
            
            const r = vertexColors.getX(i);
            const g = vertexColors.getY(i);
            
            if (g <= 0.20 || r > g) {
                if (Math.random() > 0.15) continue;
                palmCandidates.push({
                    position: new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)),
                    rotationY: Math.random() * Math.PI * 2,
                    baseScale: 0.003 + Math.random() * 0.003
                });
            }
        }
    }

    console.log(`[Palmeras] ${palmCandidates.length} candidatas fijadas localmente en su espacio correspondiente.`);

    const dummy = new THREE.Object3D();
    const clock = new THREE.Clock();
    const posGlobalPalmera = new THREE.Vector3();

    const DISTANCIA_APARICION = 0.25; 
    const DISTANCIA_MINIMA_CRECIMIENTO = 0.02;

    return function update_viento_y_proximidad(camera) {
        const elapsedTime = clock.getElapsedTime();
        palmMaterial.userData.shaderTime = elapsedTime;
        
        if (typeof terrenoShaderUniforms !== 'undefined' && terrenoShaderUniforms.uTime) {
            terrenoShaderUniforms.uTime.value = elapsedTime;
        }

        if (!camera || palmCandidates.length === 0) return;

        let activeCount = 0;
        const camPos = camera.position;

        for (let i = 0; i < palmCandidates.length; i++) {
            const palm = palmCandidates[i];

            posGlobalPalmera.copy(palm.position).applyMatrix4(terrain.matrixWorld);
            const dist = camPos.distanceTo(posGlobalPalmera);

            if (dist < DISTANCIA_APARICION) {
                dummy.position.copy(palm.position);
                dummy.rotation.y = palm.rotationY;

                let scaleFactor = 1.0 - (
                    (dist - DISTANCIA_MINIMA_CRECIMIENTO) /
                    (DISTANCIA_APARICION - DISTANCIA_MINIMA_CRECIMIENTO)
                );
                scaleFactor = THREE.MathUtils.clamp(scaleFactor, 0.001, 1.0); 

                const finalScale = palm.baseScale * scaleFactor;
                dummy.scale.set(finalScale, finalScale, finalScale);

                dummy.updateMatrix();
                instancedMesh.setMatrixAt(activeCount, dummy.matrix);

                activeCount++;
            }
        }

        instancedMesh.count = activeCount;
        instancedMesh.instanceMatrix.needsUpdate = true;
    };
}

function create_wind_material() {
    return new THREE.MeshStandardMaterial({
        color: 0x1c4426,
        roughness: 0.9,
        metalness: 0.0,
        side: THREE.DoubleSide
    });
}

function create_lowpoly_palm_geometry() {
    const finalGeometry = new THREE.BufferGeometry();
    
    const trunkGeo = new THREE.CylinderGeometry(0.06, 0.12, 2.0, 5, 1);
    trunkGeo.translate(0, 1.0, 0);

    const masterLeavesWrap = [];
    const leavesCount = 5;
    for (let i = 0; i < leavesCount; i++) {
        const leafGeo = new THREE.ConeGeometry(0.3, 1.2, 4);
        leafGeo.rotateX(Math.PI / 2.6);
        leafGeo.translate(0, 2.0, 0.4);
        leafGeo.rotateY((i * (Math.PI * 2)) / leavesCount);
        masterLeavesWrap.push(leafGeo);
    }

    const allPrimitives = [trunkGeo, ...masterLeavesWrap];
    const nonIndexedGeometries = [];
    let totalFloats = 0;

    allPrimitives.forEach(g => {
        const nonIndexedG = g.toNonIndexed(); 
        nonIndexedGeometries.push(nonIndexedG);
        totalFloats += nonIndexedG.attributes.position.array.length;
        g.dispose(); 
    });

    const positionArray = new Float32Array(totalFloats);
    let offset = 0;

    nonIndexedGeometries.forEach(g => {
        const array = g.attributes.position.array;
        positionArray.set(array, offset);
        offset += array.length;
        g.dispose(); 
    });

    finalGeometry.setAttribute('position', new THREE.BufferAttribute(positionArray, 3));
    finalGeometry.computeVertexNormals(); 

    return finalGeometry;
}